<template>
    <v-card class="elevation-0">
        <div>
            <alert-outline v-if="actionNameKey === 'Registration'">
                {{ $t('Connect_Google_Authenticator_with_User') }}
            </alert-outline>
        </div>
        <v-card-text>
            <v-form v-model="isValid" @submit.prevent>
                <text-field-label
                    :id="'uid'"
                    v-model="uid"
                    :label="$t('User_ID')"
                    :rules="[rules.maxChars(uid, 256)]"
                    :disabled="processing"
                    :prepend-icon="'person'"
                    :name="'uid'"
                    class="py-1"
                />
                <text-field-label
                    :id="'password'"
                    v-model="password"
                    :rules="[rules.maxChars(password, 255)]"
                    :label="$t('Password')"
                    :disabled="processing"
                    :prepend-icon="'lock'"
                    :name="'password'"
                    :type="'password'"
                    class="py-1"
                    @keydown.13="submitLogin"
                />
                <text-field-label
                    v-if="isMultiAuth"
                    :id="'onetime-password'"
                    v-model="onetimePassword"
                    :label="$t('Authenticate_Code')"
                    :rules="[rules.maxChars(onetimePassword, 32)]"
                    :disabled="processing"
                    :prepend-icon="'fa-key'"
                    :name="'onetime-password'"
                    :type="'password'"
                    class="py-1"
                    @keydown.13="submitLogin"
                />
                <checkbox v-if="domainName" v-model="isUseDomain" :disabled="processing" name="user-domain">
                    <template v-slot:label>
                        <div :class="`${$options.name}__BrakeText`">
                            {{ domainLabel }}
                            <tooltip-label
                                v-if="isUseDomain"
                                :message="$t('Please_enter_user_name_only_without_domain_to_user_ID')"
                                top
                            />
                        </div>
                    </template>
                </checkbox>
            </v-form>
        </v-card-text>
        <v-card-actions>
            <v-menu v-if="actionNameKey === 'Login'" offset-y>
                <template v-slot:activator="{ on }">
                    <v-btn :disabled="processing" color="accent" text v-on="on">
                        {{ $t('Can_Not_Login') }}
                    </v-btn>
                </template>
                <v-list dense min-width="13em">
                    <template v-for="item in listMenu">
                        <v-list-item :key="item.id" @click="menuSelection(item.id)">
                            <v-list-item-content>
                                <v-list-item-title class="text-caption">
                                    {{ $t(item.name) }}
                                </v-list-item-title>
                            </v-list-item-content>
                        </v-list-item>
                    </template>
                </v-list>
            </v-menu>
            <v-spacer />
            <button-app-control v-if="isSAML" :label="$t('SAML_authentication')" @click="submitSAMLAuthentication" />
            <button-app-control
                :label="actionName"
                :disabled="isDisableSubmit || isProcessing || !isValid"
                @click="submitLogin"
            />
        </v-card-actions>
    </v-card>
</template>

<script setup lang="ts">
import { computed, readonly, ref, watch } from 'vue';
import { toRefs } from '@vueuse/core';
import { useInputValidation } from '../../../composables/InputValidation';
import { useTranslation } from '../../../composables/translation';
import { AlertOutline, ButtonAppControl, TextFieldLabel, Checkbox, TooltipLabel } from '../../atoms';

interface MailMenuEntry {
    id: number;
    title: string;
    name: string;
}

/*
 * properties
 */
interface Props {
    processing?: boolean;
    domain?: string;
    samlEnabled?: boolean;
    multiAuth?: boolean;
    actionNameKey?: string;
    mailDialogMenu?: Array<MailMenuEntry>;
}
const props = withDefaults(defineProps<Props>(), {
    processing: false,
    domain: '',
    samlEnabled: false,
    multiAuth: false,
    actionNameKey: 'Login',
    mailDialogMenu: () => [],
});
const { processing, domain, samlEnabled, multiAuth, actionNameKey, mailDialogMenu } = toRefs(props);

/*
 * emits
 */
interface Emits {
    (
        e: 'login',
        param: {
            uid: string;
            password: string;
            onetimePassword: string;
        }
    ): void;
    (
        e: 'ldap-login',
        param: {
            uid: string;
            password: string;
            onetimePassword: string;
        }
    ): void;
    (e: 'open-mail-dialog', id: number): void;
    (e: 'sso'): void;
}
const emit = defineEmits<Emits>();

/*
 * composables
 */
const { rules, isValid } = useInputValidation();
const { t } = useTranslation();

/*
 * setter, getters
 */

const uid = ref('');
const password = ref('');
const onetimePassword = ref('');
const isUseDomain = ref(false);

const isProcessing = readonly(processing);
const isSAML = readonly(samlEnabled);
const domainName = readonly(domain);
const isDisableSubmit = computed(() => {
    return !uid.value || !password.value || isProcessing.value || !isValid.value;
});
const domainLabel = computed(() => t('Use_domain_user', { domain: domainName.value }));
const isMultiAuth = readonly(multiAuth);
const actionName = computed(() => t(actionNameKey.value));
const listMenu = readonly(mailDialogMenu);

/**
 * methods
 */
function submitLogin(): void {
    if (!isDisableSubmit.value) {
        if (isUseDomain.value) {
            emit('ldap-login', {
                uid: `${uid.value}@${domainName.value}`,
                password: password.value,
                onetimePassword: onetimePassword.value,
            });
        } else {
            emit('login', {
                uid: uid.value,
                password: password.value,
                onetimePassword: onetimePassword.value,
            });
        }
    }
}
function menuSelection(id: number): void {
    emit('open-mail-dialog', id);
}
function submitSAMLAuthentication(): void {
    emit('sso');
}

/*
 * watchers
 */
watch(
    domain,
    val => {
        isUseDomain.value = !!val;
    },
    { immediate: true }
);
watch(
    uid,
    () => {
        uid.value = uid.value.toLowerCase();
    },
    {
        immediate: true,
        deep: true,
    }
);
</script>

<style lang="scss" scoped>
.LoginForm {
    &__BrakeText {
        word-break: break-all;
    }
}
</style>
